/**
 * TriagePanel — right-docked collapsible panel for the Triage Collector.
 *
 * Mirrors the InsightsPanel structure (using CollapsibleSidePanel as the shell)
 * but is anchored to the right edge of the viewport.
 *
 * Features:
 *  - Manual "+" button to add a new triage item (title + description only).
 *  - Accept canvas-node drops: visual feedback when nodeDragOverTriage is active.
 *  - Drag-to-reorder cards within the panel.
 *  - Drag-to-canvas: each card exposes a handle that triggers a TriageItemDragState
 *    so Canvas.tsx can handle placing the item onto a tree node.
 *  - Edit / delete existing triage items.
 */
import React, { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useStore } from 'store/useStore';
import { TriageItemDragState, NewTriageItemPayload } from 'types';
import TriageCard from './TriageCard';
import CollapsibleSidePanel from './CollapsibleSidePanel';
import useDragSortList from 'hooks/useDragSortList';
import { DRAG_SORT_INDEX_ATTR, dragSortItemClassName, dragSortTranslateStyle } from 'utils/dragSortList';

/** Matches `.triage-list`'s own CSS `gap` (app.css) — see getDragSortTranslateY's doc comment. */
const TRIAGE_LIST_GAP_PX = 4;

// ─── Component ────────────────────────────────────────────────────────────────

export default function TriagePanel() {
    const {
        triageItems: rawItems,
        triagePanelMinimized,
        nodeDragOverTriage,
        addTriageItem,
        updateTriageItem,
        deleteTriageItem,
        reorderTriageItems,
        setTriagePanelMinimized,
        beginTriageItemDrag,
        triageItemDrag,
    } = useStore();

    const items = useMemo(() => rawItems ?? [], [rawItems]);

    // ─── Add form state ───────────────────────────────────────────────────────
    const [showAddForm, setShowAddForm] = useState(false);
    const [addForm, setAddForm] = useState({ title: '', description: '' });

    // ─── Inline editing ───────────────────────────────────────────────────────
    const [editingId, setEditingId] = useState<string | null>(null);

    // ─── Canvas drag ghost state ──────────────────────────────────────────────

    const [canvasDragMousePos, setCanvasDragMousePos] = useState<{ x: number; y: number } | null>(null);

    // ─── Ctrl key tracking (for Ctrl+drag-connect gesture on triage cards) ────

    const [ctrlHeld, setCtrlHeld] = useState(false);

    useEffect(() => {
        /** Update ctrlHeld whenever the Ctrl or Meta key is pressed/released. */
        const handleKeyChange = (e: KeyboardEvent) => {
            setCtrlHeld(e.ctrlKey || e.metaKey);
        };
        /** Clear on window blur to avoid the key appearing "stuck". */
        const handleBlur = () => setCtrlHeld(false);

        window.addEventListener('keydown', handleKeyChange);
        window.addEventListener('keyup', handleKeyChange);
        window.addEventListener('blur', handleBlur);
        return () => {
            window.removeEventListener('keydown', handleKeyChange);
            window.removeEventListener('keyup', handleKeyChange);
            window.removeEventListener('blur', handleBlur);
        };
    }, []);

    // The item currently being dragged to the canvas (null if none active)
    const draggingItem = triageItemDrag
        ? (items.find((i) => i.id === triageItemDrag.itemId) ?? null)
        : null;

    // Show a ghost card following the cursor and change the body cursor while in canvas drag mode
    useEffect(() => {
        if (!triageItemDrag) {
            setCanvasDragMousePos(null);
            return;
        }
        // Initialise ghost at the mousedown start position before any mousemove fires
        setCanvasDragMousePos({ x: triageItemDrag.startScreenX, y: triageItemDrag.startScreenY });

        const onMove = (e: MouseEvent) => {
            setCanvasDragMousePos({ x: e.clientX, y: e.clientY });
        };

        // Use the pinch cursor for Ctrl-initiated drag-connect gestures; grabbing for the
        // legacy ↗ drag-handle flow to preserve existing visual affordances for each mode.
        const pinchCursorUrl = `${process.env.PUBLIC_URL || ''}/hand-pinch-cursor.cur`;
        document.body.style.cursor = triageItemDrag.isCtrlDrag
            ? `url(${pinchCursorUrl}) 16 16, crosshair`
            : 'grabbing';
        window.addEventListener('mousemove', onMove);

        return () => {
            window.removeEventListener('mousemove', onMove);
            document.body.style.cursor = '';
        };
    }, [triageItemDrag]);

    // ─── Drag-to-reorder state ────────────────────────────────────────────────
    const panelRef = useRef<HTMLDivElement>(null);

    // ─── Handlers: add form ───────────────────────────────────────────────────

    const handleAddFormChange = useCallback(
        (field: 'title' | 'description', value: string) => {
            setAddForm((prev) => ({ ...prev, [field]: value }));
        },
        [],
    );

    const handleSubmit = useCallback(() => {
        const title = addForm.title.trim();
        if (!title) return;
        const payload: NewTriageItemPayload = {
            title,
            description: addForm.description.trim(),
        };
        addTriageItem(payload);
        setAddForm({ title: '', description: '' });
        setShowAddForm(false);
    }, [addForm, addTriageItem]);

    const handleCancelAdd = useCallback(() => {
        setAddForm({ title: '', description: '' });
        setShowAddForm(false);
    }, []);

    const handleToggleEdit = useCallback((id: string) => {
        setEditingId((prev) => (prev === id ? null : id));
    }, []);

    // ─── Handlers: card drag-to-reorder ──────────────────────────────────────

    const handleReorder = useCallback(
        (fromIndex: number, toIndex: number) => reorderTriageItems(fromIndex, toIndex),
        [reorderTriageItems],
    );
    const { dragState, beginDrag, getTranslateY } = useDragSortList({
        itemCount: items.length,
        containerRef: panelRef,
        onReorder: handleReorder,
        gap: TRIAGE_LIST_GAP_PX,
    });

    const handleReorderDragMouseDown = useCallback(
        (e: React.MouseEvent, itemId: string) => {
            const index = items.findIndex((n) => n.id === itemId);
            beginDrag(e, index);
        },
        [items, beginDrag],
    );

    // ─── Handlers: drag-to-canvas ─────────────────────────────────────────────

    const handleDragToCanvasMouseDown = useCallback(
        (clientX: number, clientY: number, itemId: string, isCtrlDrag?: boolean) => {
            if (dragState) return; // don't start a canvas drag while reordering
            const drag: TriageItemDragState = {
                itemId,
                startScreenX: clientX,
                startScreenY: clientY,
                isCtrlDrag,
            };
            beginTriageItemDrag(drag);
        },
        [beginTriageItemDrag, dragState],
    );

    const grabbingCursor = `url(${process.env.PUBLIC_URL || ''}/hand-pinch-cursor.cur) 16 16, grabbing`;

    // ─── Collapse logic ───────────────────────────────────────────────────────

    const isCollapsed = triagePanelMinimized;
    const toggleCollapse = useCallback(
        () => setTriagePanelMinimized(!isCollapsed),
        [isCollapsed, setTriagePanelMinimized],
    );

    // ─── Render ───────────────────────────────────────────────────────────────

    return (
        <CollapsibleSidePanel
            ref={panelRef}
            side='right'
            isCollapsed={isCollapsed}
            onToggleCollapse={toggleCollapse}
            className={`triage-panel ${nodeDragOverTriage ? 'triage-panel--drop-target triage-panel--node-dragging' : ''}`}
            ariaLabelOpen='Open triage'
            ariaLabelClose='Collapse triage'
        >
            <div className='triage-panel-inner'>
                {/* Panel header */}
                <div className='triage-panel-header'>
                    <span className='triage-panel-title'>
                        Triage
                        {items.length > 0 && (
                            <span className='triage-panel-count'>{items.length}</span>
                        )}
                    </span>

                    <div className='triage-panel-header-actions'>
                        {!isCollapsed && (
                            <button
                                className='triage-header-btn'
                                onClick={() => setShowAddForm((v) => !v)}
                                title='Add triage item'
                                aria-label='Add triage item'
                            >
                                +
                            </button>
                        )}
                        <button
                            className='triage-header-btn triage-header-btn--close'
                            onClick={() => setTriagePanelMinimized(true)}
                            title='Close triage'
                            aria-label='Close triage'
                        >
                            ›
                        </button>
                    </div>
                </div>

                {!isCollapsed && (
                    <div
                        className={`triage-panel-body ${nodeDragOverTriage ? 'triage-panel-body--drop-active' : ''}`}
                    >
                        {/* Add form */}
                        {showAddForm && (
                            <div className='triage-add-form'>
                                <label className='triage-field-label'>Title *</label>
                                <input
                                    className='triage-field-input'
                                    value={addForm.title}
                                    onChange={(e) => handleAddFormChange('title', e.target.value)}
                                    placeholder='Triage item title'
                                    autoFocus
                                    maxLength={200}
                                />

                                <label className='triage-field-label'>Description</label>
                                <textarea
                                    className='triage-field-textarea'
                                    value={addForm.description}
                                    onChange={(e) =>
                                        handleAddFormChange('description', e.target.value)
                                    }
                                    placeholder='Describe this item…'
                                    rows={3}
                                    maxLength={500}
                                />
                                <span className='triage-field-char-count'>
                                    {(addForm.description || '').length} / 500
                                </span>

                                <div className='triage-form-actions'>
                                    <button
                                        className='triage-btn triage-btn--save'
                                        onClick={handleSubmit}
                                        disabled={!addForm.title.trim()}
                                    >
                                        Add
                                    </button>
                                    <button
                                        className='triage-btn triage-btn--cancel'
                                        onClick={handleCancelAdd}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Empty state */}
                        {items.length === 0 && !showAddForm && (
                            <p className='triage-empty-hint'>
                                No triage items. Click + to add one, or drag a node here.
                            </p>
                        )}

                        {/* Node-drop hint when a canvas node is being dragged over */}
                        {nodeDragOverTriage && (
                            <div className='triage-drop-hint'>
                                Release to add to Triage
                            </div>
                        )}

                        {/* Cards list */}
                        <div className='triage-list'>
                            {items.map((item) => {
                                const idx = items.findIndex((t) => t.id === item.id);
                                const translateY = getTranslateY(idx);
                                const isBeingDragged = dragState?.fromIndex === idx;
                                return (
                                    <div
                                        key={item.id}
                                        {...{ [DRAG_SORT_INDEX_ATTR]: idx }}
                                        className={dragSortItemClassName('triage-list-item', !!dragState, isBeingDragged)}
                                        style={dragSortTranslateStyle(translateY)}
                                    >
                                        <TriageCard
                                            item={item}
                                            isEditing={editingId === item.id}
                                            onToggleEdit={handleToggleEdit}
                                            onUpdate={updateTriageItem}
                                            onDelete={deleteTriageItem}
                                            onDragHandleMouseDown={handleReorderDragMouseDown}
                                            onDragToCanvasMouseDown={handleDragToCanvasMouseDown}
                                            isCtrlHeld={ctrlHeld}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>

            {/* Ghost card rendered via portal during drag-to-canvas */}
            {triageItemDrag && draggingItem && canvasDragMousePos &&
                createPortal(
                    <div
                        className='triage-drag-ghost triage-drag-ghost--canvas drag-ghost cursor-grabbing'
                        style={{
                            '--ghost-top': `${canvasDragMousePos.y - 28}px`,
                            '--ghost-left': `${canvasDragMousePos.x - 20}px`,
                            '--ghost-width': '200px',
                            pointerEvents: 'none',
                            cursor: grabbingCursor,
                        } as React.CSSProperties}
                    >
                        <TriageCard
                            item={draggingItem}
                            isEditing={false}
                            onToggleEdit={() => { }}
                            onUpdate={() => { }}
                            onDelete={() => { }}
                            onDragHandleMouseDown={() => { }}
                            onDragToCanvasMouseDown={() => { }}
                            isCtrlHeld={false}
                        />
                    </div>,
                    document.body,
                )}

            {/* Ghost card rendered via portal during drag-to-reorder */}
            {dragState && items[dragState.fromIndex] &&
                createPortal(
                    <div
                        className='triage-drag-ghost drag-ghost cursor-grabbing'
                        style={{
                            '--ghost-top': `${dragState.mouseY - dragState.offsetY}px`,
                            '--ghost-left': `${dragState.ghostX}px`,
                            '--ghost-width': `${dragState.ghostWidth}px`,
                            cursor: grabbingCursor,
                        } as React.CSSProperties}
                    >
                        <TriageCard
                            item={items[dragState.fromIndex]}
                            isEditing={false}
                            onToggleEdit={() => { }}
                            onUpdate={() => { }}
                            onDelete={() => { }}
                            onDragHandleMouseDown={() => { }}
                            onDragToCanvasMouseDown={() => { }}
                            isCtrlHeld={false}
                        />
                    </div>,
                    document.body,
                )}
        </CollapsibleSidePanel>
    );
}
